import { cn } from "./cn";

// A person's initials on one of the 12 avatar colours (`--avatar-1` ... `--avatar-12` of the design tokens). The colour is a stable function
// of the person id, so one person has the same colour on every screen, in every session and on every machine. The initials are decoration:
// the name is always written next to an avatar, so a screen reader skips it.

/** The 12 colours as class names. Tailwind finds a class only when it is written out in full, so the list is literal. */
const AVATAR_BACKGROUNDS = [
  "bg-avatar-1",
  "bg-avatar-2",
  "bg-avatar-3",
  "bg-avatar-4",
  "bg-avatar-5",
  "bg-avatar-6",
  "bg-avatar-7",
  "bg-avatar-8",
  "bg-avatar-9",
  "bg-avatar-10",
  "bg-avatar-11",
  "bg-avatar-12",
] as const;

/** FNV-1a (32 bit) over the UTF-16 code units of the id. It has no seed and no platform input, so every browser gives the same number. */
function hash(id: string): number {
  let value = 0x811c9dc5;
  for (let index = 0; index < id.length; index += 1) {
    value ^= id.charCodeAt(index);
    value = Math.imul(value, 0x01000193);
  }
  return value >>> 0;
}

/** The colour slot of a person, 0 to 11. The same id always gives the same slot. Do not change the hash: it would recolour every person. */
export function avatarColourIndex(id: string): number {
  return hash(id) % AVATAR_BACKGROUNDS.length;
}

/** `Maya Chen` gives `MC`, `Anika` gives `AN`, a name of three words uses the first and the last. A blank name gives `?`. */
export function initialsOf(name: string): string {
  const [first, ...rest] = name.trim().split(/\s+/u);
  if (first === undefined || first === "") return "?";
  const last = rest.at(-1);
  const letters =
    last === undefined
      ? Array.from(first).slice(0, 2)
      : [Array.from(first)[0], Array.from(last)[0]];
  return letters.join("").toLocaleUpperCase();
}

const sizes = {
  sm: "size-8 text-xs",
  md: "size-10 text-sm",
  lg: "size-12 text-base",
} as const;

type AvatarProps = Readonly<{
  /** The id of the person (staff user id or customer id). It picks the colour. It is never shown. */
  id: string;
  /** The name that the initials come from. Write the name next to the avatar too. */
  name: string;
  size?: keyof typeof sizes;
  className?: string;
}>;

export function Avatar({ id, name, size = "md", className }: AvatarProps) {
  return (
    <span
      aria-hidden="true"
      data-avatar-slot={avatarColourIndex(id) + 1}
      className={cn(
        "inline-grid shrink-0 place-items-center rounded-full font-semibold text-avatar-foreground select-none",
        AVATAR_BACKGROUNDS[avatarColourIndex(id)],
        sizes[size],
        className,
      )}
    >
      {initialsOf(name)}
    </span>
  );
}
